import { useEffect } from "react";
import { useScene } from "~/lib/store";

const ANIMATION_MS = 420;

/**
 * Easter egg reveal modal — triggered by clicking the hidden 9th cubie
 * that appears once the visitor has typed the Konami code with all eight
 * cubies already visited. More casual than TrustUnlocked: handwritten
 * voice, less-formal contact channels.
 */
export function EasterEggReveal() {
  const open = useScene((s) => s.easterEggRevealOpen);
  const close = useScene((s) => s.closeEasterEggReveal);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="easter-egg-title"
      className="fixed inset-0 z-[90] flex items-center justify-center px-4 py-8 bg-jg-bg/85 backdrop-blur-md transition-opacity"
      style={{ transitionDuration: `${ANIMATION_MS}ms` }}
      onClick={close}
    >
      <div
        className="relative w-full max-w-lg rounded-3xl border border-jg-border bg-jg-bg-soft/95 p-7 shadow-[0_30px_120px_-20px_rgba(255,255,255,0.25)] sm:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-jg-fg">
          9th cubie · classified
        </p>

        <h2
          id="easter-egg-title"
          className="mt-3 font-display text-2xl font-semibold leading-tight tracking-tight sm:text-3xl"
        >
          Hey — you typed the Konami code.
        </h2>

        <p className="mt-3 text-sm leading-relaxed text-jg-fg-muted sm:text-base">
          That means you're either a friend, a fellow nerd, or a very
          thorough recruiter. Either way we'd probably get along. For the
          less-formal channels:
        </p>

        <ul className="mt-5 grid gap-2 sm:grid-cols-3">
          <li>
            <a
              href="https://github.com/jgvidotto"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl border border-jg-border bg-jg-bg-soft/60 p-3 text-center text-sm font-medium text-jg-fg-muted transition-colors hover:border-jg-ai/40 hover:text-jg-fg"
            >
              GitHub
            </a>
          </li>
          <li>
            <a
              href="https://www.linkedin.com/in/jgvidotto/"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl border border-jg-border bg-jg-bg-soft/60 p-3 text-center text-sm font-medium text-jg-fg-muted transition-colors hover:border-jg-ai/40 hover:text-jg-fg"
            >
              LinkedIn
            </a>
          </li>
          {/* TODO João: drop your real Discord invite URL here (or X / Bluesky / whatever you prefer). */}
          <li>
            <span className="block rounded-xl border border-dashed border-jg-border bg-jg-bg-soft/20 p-3 text-center text-sm text-jg-fg-muted">
              Discord · soon
            </span>
          </li>
        </ul>

        <p className="mt-6 font-mono text-xs text-jg-fg-muted">
          — João
        </p>

        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full p-1 text-jg-fg-muted hover:text-jg-fg"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  );
}
